// The customer's NIF on a case: read off the uploaded Finanças confirmation by
// the backend, confirmed or corrected by the lawyer here. Same conventions as
// the other case endpoints; the shared helper lives in ./client.

import { request } from './client';

export type NifExtractionStatus = 'extracted' | 'review' | 'no_nif' | 'failed';

export interface NifExtraction {
  status: NifExtractionStatus;
  nif: string;
  full_name?: string | null;
  issue_date?: string | null;
  confidence?: number;
  is_nif_confirmation?: boolean;
  check_digit_valid?: boolean;
  name_matches?: boolean | null;
  review_needed: boolean;
  reason?: string;
  model?: string;
  document_id?: string;
  file_name?: string;
  extracted_at?: any;
  resolved_by?: string;
}

export interface NifNumberResult {
  nif_number: string;
  changed: boolean;
}

// The backend validates the check digit and answers 400 with a readable
// `detail` when the number cannot be a NIF; the page shows that line as is.
export const setCaseNifNumber = async (
  token: string,
  caseId: string,
  nifNumber: string
): Promise<NifNumberResult> =>
  request(
    token,
    `/cases/${encodeURIComponent(caseId)}/nif-number`,
    { method: 'PUT', body: JSON.stringify({ nif_number: nifNumber }) },
    'Failed to save the NIF.'
  );

// 293 471 606, the way Finanças prints it. Anything that is not nine digits is
// shown as typed.
export const formatNif = (value: string | null | undefined): string => {
  const digits = (value ?? '').replace(/\D+/g, '');
  if (digits.length !== 9) return value ?? '';
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

// The leading digits Finanças issues (1, 2, 3 and 45 for people, the rest for
// companies and other entities); 0, 4x other than 45, 73, 76 and 92 to 97 are
// never NIFs. The backend enforces the same list.
const NIF_PREFIXES = /^(1|2|3|5|6|8|45|70|71|72|74|75|77|78|79|90|91|98|99)/;

// A real NIF: nine digits, an issued prefix, and the Portuguese check digit
// (weights 9..2 over the first eight digits, mod 11), so a typo is caught
// before the request leaves the browser.
export const isValidNif = (value: string | null | undefined): boolean => {
  const digits = (value ?? '').replace(/\D+/g, '');
  if (digits.length !== 9 || !NIF_PREFIXES.test(digits)) return false;
  let total = 0;
  for (let i = 0; i < 8; i += 1) total += Number(digits[i]) * (9 - i);
  const remainder = total % 11;
  const expected = remainder < 2 ? 0 : 11 - remainder;
  return Number(digits[8]) === expected;
};
